import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close as CloseIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * RightPanel
 * ----------
 * Right-side slide-in panel. Hidden by default; pages can open it to display
 * contextual detail (e.g. selected node info, article digest, etc.).
 *
 * Pages should drive the panel via the `useRightPanel` hook:
 *
 *   const { openPanel, closePanel } = useRightPanel();
 *   openPanel(<MyDetail/>, { title: 'Method detail' });
 *
 * Width: 320px when open. Animates from the right edge.
 */

export const RIGHT_PANEL_WIDTH = 320;
export const RIGHT_PANEL_STORAGE_KEY = 'mvkg.rightPanel.w';
export const RIGHT_PANEL_MIN_WIDTH = 300;
export const RIGHT_PANEL_MAX_WIDTH = 760;
export const RIGHT_PANEL_DEFAULT_WIDTH = 420;
export const RIGHT_PANEL_DESKTOP_RIGHT = 14;

export function clampRightPanelWidth(value) {
  return Number.isFinite(value) && value >= RIGHT_PANEL_MIN_WIDTH
    ? Math.min(value, RIGHT_PANEL_MAX_WIDTH)
    : RIGHT_PANEL_DEFAULT_WIDTH;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const RightPanelContext = createContext({
  open: false,
  content: null,
  title: '',
  variant: 'default',
  openPanel: () => {},
  closePanel: () => {},
});

export function RightPanelProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(null);
  const [title, setTitle] = useState('');
  const [variant, setVariant] = useState('default');
  // `bare` = content provides its own header/close (e.g. graph DetailPanel), so
  // the panel must NOT render a second header+✕ (that's the dead duplicate ✕).
  const [bare, setBare] = useState(false);
  // Opener-supplied close callback. Crucial: the graph page drives the panel
  // from its `selectedItem` state, so closing the panel (click-away / Esc / ✕)
  // must also clear that state — otherwise the page re-opens it immediately.
  const onCloseRef = useRef(null);
  const clearTimerRef = useRef(null);

  const openPanel = useCallback((node, opts = {}) => {
    if (clearTimerRef.current) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
    setContent(node);
    setTitle(opts.title || '');
    setBare(!!opts.bare);
    setVariant(opts.variant || 'default');
    onCloseRef.current = opts.onClose || null;
    setOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
    if (onCloseRef.current) {
      const cb = onCloseRef.current;
      onCloseRef.current = null;
      try { cb(); } catch (_) { /* ignore */ }
    }
    // Keep content briefly so exit animation can show — clear after a tick.
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => {
      setContent(null);
      setTitle('');
      setBare(false);
      setVariant('default');
      clearTimerRef.current = null;
    }, 200);
  }, []);

  useEffect(() => () => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
  }, []);

  const value = useMemo(
    () => ({ open, content, title, bare, variant, openPanel, closePanel }),
    [open, content, title, bare, variant, openPanel, closePanel]
  );

  return (
    <RightPanelContext.Provider value={value}>
      {children}
    </RightPanelContext.Provider>
  );
}

export function useRightPanel() {
  return useContext(RightPanelContext);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function RightPanel({ open: openProp, children, title: titleProp, onClose }) {
  // The component can be used controlled (via props) or driven by the context.
  const ctx = useRightPanel();
  const isOpen = openProp !== undefined ? openProp : ctx.open;
  const content = children !== undefined ? children : ctx.content;
  const headerTitle = titleProp !== undefined ? titleProp : ctx.title;
  const handleClose = onClose || ctx.closePanel;
  const isBare = !!ctx.bare;  // content owns its own header/close → don't add ours
  const panelVariant = ctx.variant || 'default';
  const isGraphPanel = panelVariant === 'graph' || panelVariant === 'galaxy';
  const isGalaxyPanel = panelVariant === 'galaxy';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const asideRef = useRef(null);

  // Resizable desktop width (drag the left edge; persisted). Default wider than the
  // old fixed 320 so the AI insight panel + tables aren't cramped.
  const [panelW, setPanelW] = useState(() => {
    const v = parseInt(localStorage.getItem(RIGHT_PANEL_STORAGE_KEY), 10);
    return clampRightPanelWidth(v);
  });
  const [viewportW, setViewportW] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth : 1440
  ));
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const effectivePanelW = useMemo(() => {
    if (!isGraphPanel || isMobile) return panelW;
    const maxRatio = isGalaxyPanel ? 0.25 : 0.28;
    const viewportCap = Math.floor(Math.max(1, viewportW) * maxRatio);
    const dynamicMax = Math.min(RIGHT_PANEL_MAX_WIDTH, Math.max(RIGHT_PANEL_MIN_WIDTH, viewportCap));
    return Math.min(panelW, dynamicMax);
  }, [isGraphPanel, isGalaxyPanel, isMobile, panelW, viewportW]);
  useEffect(() => { try { localStorage.setItem(RIGHT_PANEL_STORAGE_KEY, String(panelW)); } catch (e) { /* quota */ } }, [panelW]);
  useEffect(() => {
    const layoutOpen = Boolean(isOpen && isGraphPanel && !isMobile);
    const width = layoutOpen ? effectivePanelW : 0;
    try {
      document.documentElement.style.setProperty('--mvkg-right-panel-width', `${width}px`);
      document.documentElement.style.setProperty('--mvkg-right-panel-right', `${RIGHT_PANEL_DESKTOP_RIGHT}px`);
    } catch (_) { /* ignore */ }
    try {
      window.dispatchEvent(new CustomEvent('mvkg:right-panel-layout', {
        detail: {
          open: layoutOpen,
          width,
          right: RIGHT_PANEL_DESKTOP_RIGHT,
          variant: panelVariant,
        },
      }));
    } catch (_) { /* ignore */ }
  }, [isOpen, isGraphPanel, isMobile, effectivePanelW, panelVariant]);
  const wDrag = useRef(null);
  const startWResize = (e) => {
    e.preventDefault();
    wDrag.current = { x: e.clientX, w: panelW };
    const move = (ev) => {
      if (!wDrag.current) return;
      const dx = wDrag.current.x - ev.clientX; // drag LEFT → wider
      setPanelW(clampRightPanelWidth(wDrag.current.w + dx));
    };
    const up = () => {
      wDrag.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // Dismiss on Escape, and on a click outside the panel. Clicks on the graph
  // <canvas> are ignored so picking another node still works (the graph drives
  // selection); clicking page chrome / empty space closes the panel.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    const onDown = (e) => {
      const aside = asideRef.current;
      if (!aside || aside.contains(e.target)) return;
      // Ignore clicks on the graph itself: the sigma <canvas> (node/edge pick)
      // and DOM overlays that drive selection (e.g. clickable cluster pills,
      // marked data-graph-overlay). Otherwise mousedown here would close the
      // panel and race the new selection → "can't click another cluster".
      if (e.target.closest && e.target.closest('canvas, [data-graph-overlay]')) return;
      handleClose();
    };
    document.addEventListener('keydown', onKey);
    // mousedown on capture so it runs before the graph's own handlers
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [isOpen, handleClose]);

  const graphGlass = isGalaxyPanel
    ? {
        background: 'linear-gradient(180deg, rgba(13,15,28,0.9) 0%, rgba(7,9,18,0.84) 100%)',
        border: '1px solid rgba(167,139,250,0.3)',
        boxShadow: '-28px 0 72px rgba(0,0,0,0.42), 0 0 0 1px rgba(255,255,255,0.04) inset',
        color: '#E8E4F0',
      }
    : {
        // Non-galaxy 2D graph: LIGHT glass card. Dark glass is galaxy-only.
        background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,249,252,0.96) 100%)',
        border: '1px solid rgba(15,23,42,0.10)',
        boxShadow: '-24px 0 56px rgba(15,23,42,0.14), 0 0 0 1px rgba(255,255,255,0.6) inset',
        color: '#1a1a2e',
      };

  // On mobile the panel must NOT be an inline flex sibling (it would force
  // horizontal overflow); render it as a fixed overlay sliding in from the right
  // over the content, with a tap-to-dismiss backdrop.
  const mobileStyle = {
    position: 'fixed',
    top: 72,
    right: isGraphPanel ? 8 : 0,
    bottom: isGraphPanel ? 8 : 0,
    width: 'min(420px, 92vw)',
    zIndex: theme.zIndex.drawer + 2,
    overflow: 'hidden',
    background: isGraphPanel ? graphGlass.background : '#ffffff',
    borderLeft: isGraphPanel ? graphGlass.border : '1px solid rgba(0,0,0,0.06)',
    borderRadius: isGraphPanel ? 20 : 0,
    boxShadow: isGraphPanel ? graphGlass.boxShadow : '-8px 0 24px rgba(0,0,0,0.18)',
    backdropFilter: isGraphPanel ? 'blur(18px) saturate(140%)' : undefined,
    color: graphGlass.color,
    display: 'flex',
    flexDirection: 'column',
  };
  const desktopStyle = isGraphPanel ? {
    flexShrink: 0,
    overflow: 'hidden',
    background: graphGlass.background,
    border: graphGlass.border,
    height: 'auto',
    position: 'fixed',
    top: isGalaxyPanel ? 'var(--mvkg-galaxy-panel-top, 156px)' : 84,
    right: RIGHT_PANEL_DESKTOP_RIGHT,
    bottom: isGalaxyPanel ? 44 : 14,
    borderRadius: 20,
    display: 'flex',
    flexDirection: 'column',
    zIndex: theme.zIndex.drawer + 1,
    boxShadow: graphGlass.boxShadow,
    backdropFilter: 'blur(18px) saturate(140%)',
    color: graphGlass.color,
  } : {
    flexShrink: 0,
    overflow: 'hidden',
    background: '#ffffff',
    borderLeft: '1px solid rgba(0,0,0,0.06)',
    height: 'calc(100vh - 72px)',
    position: 'sticky',
    top: 72,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 30,
  };

  return (
    <AnimatePresence>
      {isOpen && isMobile && (
        <motion.div
          key="right-panel-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15,23,42,0.38)',
            zIndex: theme.zIndex.drawer + 1,
          }}
        />
      )}
      {isOpen && (
        <motion.aside
          key="right-panel"
          ref={asideRef}
          data-testid="right-panel"
          initial={isMobile ? { x: '100%', opacity: 1 } : { width: 0, opacity: 0 }}
          animate={isMobile ? { x: 0, opacity: 1 } : { width: effectivePanelW, opacity: 1 }}
          exit={isMobile ? { x: '100%', opacity: 1 } : { width: 0, opacity: 0 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          style={isMobile ? mobileStyle : desktopStyle}
        >
          {!isMobile && (
            <Box
              onPointerDown={startWResize}
              title="Drag to resize panel"
              sx={{
                position: 'absolute',
                left: -7,
                top: isGalaxyPanel ? 12 : 0,
                bottom: isGalaxyPanel ? 12 : 0,
                width: 14,
                zIndex: 6,
                cursor: 'ew-resize',
                borderRadius: 999,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: '50%',
                  top: isGalaxyPanel ? 14 : 0,
                  bottom: isGalaxyPanel ? 14 : 0,
                  width: 3,
                  transform: 'translateX(-50%)',
                  borderRadius: 999,
                  bgcolor: isGalaxyPanel ? 'rgba(232,228,240,0.36)' : 'rgba(15,23,42,0.18)',
                  boxShadow: isGalaxyPanel ? '0 0 14px rgba(167,139,250,0.42)' : 'none',
                },
                '&:hover': {
                  bgcolor: isGalaxyPanel ? 'rgba(167,139,250,0.12)' : 'rgba(15,23,42,0.06)',
                  '&::before': {
                    bgcolor: isGalaxyPanel ? 'rgba(232,228,240,0.78)' : 'rgba(15,23,42,0.34)',
                  },
                },
              }}
            />
          )}
          {!isBare && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: isGalaxyPanel ? 'rgba(138,130,160,0.24)' : 'divider',
                bgcolor: isGalaxyPanel ? 'rgba(7,9,18,0.32)' : 'transparent',
                minHeight: 48,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {headerTitle || 'Details'}
              </Typography>
              <Tooltip title="Close">
                <IconButton size="small" onClick={handleClose} aria-label="close right panel">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              height: '100%',
              overflowY: isBare && isGraphPanel ? 'hidden' : 'auto',
              overflowX: 'hidden',
              p: isBare ? 0 : 2,
              ...(isGraphPanel ? {
                scrollbarColor: 'rgba(167,139,250,0.45) rgba(255,255,255,0.08)',
                '&::-webkit-scrollbar': { width: 10 },
                '&::-webkit-scrollbar-track': { bgcolor: 'rgba(255,255,255,0.04)' },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'rgba(167,139,250,0.35)',
                  borderRadius: 999,
                },
              } : {}),
            }}
          >
            {content}
          </Box>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
